// The org admin's shared capabilities: one Development set and one Standards
// set per organization, each capability with its four levels and the roles it
// applies to (or every role, those added later too). They are set up as a
// team's role is (the same rules, ./capabilitySetup.ts, on the set seen as a
// role: setAsRole), and confirmed the same way: capabilities, then levels. Once
// a set's levels are confirmed, each of its capabilities is part of every role
// it applies to, read-only there; the team never confirms or edits it. A role
// with only shared capabilities is still confirmed by its manager (its
// capabilities, which confirms its levels too) before it is ready.
//
// One name means one capability: a shared capability's name is not used by the
// other set, nor by a capability of its own in any team it applies to, and a
// team cannot give a role its own capability under the name of a shared one
// that applies in that team. The backend keeps the sets and applies the same
// rules (docs/PRD-capabilities-roundup.md).
//
// No React here, so it can be tested on its own (sharedSets.test.ts).

import {
  countedCapabilities,
  hasAllLevels,
  isRoleReady,
  type LevelCriteria,
  type SetupCapability,
  type SetupRole
} from './capabilitySetup';
import { SOURCE_LABEL, isSharedKind, type CapabilitySource, type SharedKind } from './capabilitySource';

export interface SetCapability {
  id: string;
  name: string;
  criteria: LevelCriteria;
  /** The roles it applies to, by id. */
  roleIds: string[];
  /** It applies to every role, those added later too. */
  allRoles: boolean;
  /** The org admin who last applied or changed it. */
  setBy?: { id: string; name: string } | null;
}

export interface CapabilitySet {
  kind: SharedKind;
  capabilities: SetCapability[];
  capabilitiesConfirmed?: boolean;
  levelsConfirmed?: boolean;
  /** Its levels have been confirmed at least once: a capability can then only be taken off roles, not deleted. */
  established?: boolean;
  /** As a role's: one more with every saved change, sent with the next. */
  version?: number;
}

/** A team-like name for the set's id when it is edited as a role. */
export const sharedSetId = (kind: SharedKind) => `set:${kind}`;

/** The set a role-shaped id stands for, or null when it is a team's role. */
export const kindOfRoleId = (id: string | null | undefined): SharedKind | null => {
  const kind = id?.startsWith('set:') ? id.slice(4) : null;
  return isSharedKind(kind) ? kind : null;
};

export const emptySet = (kind: SharedKind): CapabilitySet => ({ kind, capabilities: [] });

/** The set as a role, to be edited with the role's rules. Each capability keeps who it applies to. */
export const setAsRole = (set: CapabilitySet): SetupRole => ({
  id: sharedSetId(set.kind),
  teamId: sharedSetId(set.kind),
  name: SOURCE_LABEL[set.kind],
  capabilities: set.capabilities.map((c) => ({
    id: c.id,
    name: c.name,
    criteria: c.criteria,
    roleIds: c.roleIds,
    allRoles: c.allRoles,
    setBy: c.setBy ?? null
  })),
  capabilitiesConfirmed: set.capabilitiesConfirmed,
  levelsConfirmed: set.levelsConfirmed,
  established: set.established,
  version: set.version,
  unlimited: true
});

/** The set a role made by setAsRole stands for, as edited. */
export const setFromRole = (kind: SharedKind, role: SetupRole): CapabilitySet => ({
  kind,
  capabilities: role.capabilities.map((c) => ({
    id: c.id,
    name: c.name,
    criteria: c.criteria,
    roleIds: c.roleIds ?? [],
    allRoles: Boolean(c.allRoles),
    setBy: c.setBy ?? null
  })),
  capabilitiesConfirmed: role.capabilitiesConfirmed,
  levelsConfirmed: role.levelsConfirmed,
  established: role.established,
  version: role.version
});

/** The set's capabilities apply to roles once its levels are confirmed. */
export const isSetReady = (set: CapabilitySet | null | undefined): boolean =>
  Boolean(set?.capabilitiesConfirmed && set.levelsConfirmed);

/** Whether a shared capability applies to the role. */
export const appliesTo = (capability: Pick<SetupCapability, 'roleIds' | 'allRoles'>, role: { id: string }): boolean =>
  Boolean(capability.allRoles) || (capability.roleIds ?? []).includes(role.id);

/** Who a shared capability applies to, in a few words. */
export const appliesLabel = (capability: Pick<SetupCapability, 'roleIds' | 'allRoles'>, roles: SetupRole[]): string => {
  if (capability.allRoles) return 'All roles';
  const names = roles.filter((r) => appliesTo(capability, r)).map((r) => r.name);
  if (names.length === 0) return 'No roles yet';
  return names.length <= 2 ? names.join(', ') : `${names.length} roles`;
};

/**
 * The shared capabilities that are part of a role: every one that applies to
 * it, with all four levels, of a set whose levels have been confirmed (at least
 * once: one added later and still being reviewed applies to no role until the
 * org admin chooses its roles), with where it comes from and who set it.
 */
export const appliedTo = (role: { id: string }, sets: CapabilitySet[]): SetupCapability[] =>
  sets
    .filter((set) => isSetReady(set) || set.established)
    .flatMap((set) =>
      set.capabilities
        .filter((c) => appliesTo(c, role) && hasAllLevels(c))
        .map((c) => ({ id: c.id, name: c.name, criteria: c.criteria, source: set.kind, setBy: c.setBy ?? null }))
    );

/** A role's capabilities from one source: its own ('role'), or the shared ones of that set applied to it. */
export const capabilitiesFrom = (role: SetupRole, sets: CapabilitySet[], source: CapabilitySource): SetupCapability[] =>
  source === 'role'
    ? role.capabilities.map((c) => ({ ...c, source: 'role' as const }))
    : appliedTo(role, sets).filter((c) => c.source === source);

/**
 * What counts on a role, as Assessments and Trainings build on it: its own
 * capabilities that count (confirmed: ./capabilitySetup.ts countedCapabilities),
 * then the shared ones applied to it.
 */
export const countedCapabilitiesOf = (role: SetupRole, sets: CapabilitySet[]): SetupCapability[] => [
  ...countedCapabilities(role).map((c) => ({ ...c, source: 'role' as const })),
  ...appliedTo(role, sets)
];

/**
 * A role can be assessed, trained on and invited to once its manager has
 * confirmed it (with only shared capabilities, its capabilities, which
 * confirms its levels too), and something counts on it: a confirmed capability
 * of its own or a shared one applied. It stays so while its team adds
 * capabilities. The backend's `ready` says so for every role it answers for
 * (RoleInfo.ready, what Invite Team and Update Team offer); the same rule
 * over the sets stands for a role it hasn't.
 */
export const isRoleUsable = (role: SetupRole, sets: CapabilitySet[]): boolean =>
  role.ready ?? isRoleReady(role, appliedTo(role, sets).length);

const tidy = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase();
const sameName = (a: string, b: string) => tidy(a) === tidy(b);

/** Why a shared capability can't have its name: the other set has one called that. Null when it can. */
export const setNameProblem = (sets: CapabilitySet[], kind: SharedKind, name: string): string | null => {
  if (!name.trim()) return null;
  const other = sets.find((s) => s.kind !== kind && s.capabilities.some((c) => sameName(c.name, name)));
  return other ? `${SOURCE_LABEL[other.kind]} already has a capability called ${name.trim()}.` : null;
};

/**
 * Why a shared capability can't apply to the roles it is given, or null when
 * it can: a team of one of them already has a capability of its own under that
 * name. `teamName` names a team by id.
 */
export const applyProblem = (
  capability: Pick<SetupCapability, 'name' | 'roleIds' | 'allRoles'>,
  roles: SetupRole[],
  teamName: (teamId: string) => string
): string | null => {
  if (!capability.name.trim()) return null;
  const clashing = clashingRoleIds(roles, capability.name);
  const teams = Array.from(
    new Set(roles.filter((r) => appliesTo(capability, r) && clashing.has(r.id)).map((r) => r.teamId))
  );
  if (teams.length === 0) return null;
  const names = teams.map(teamName);
  return `${names.join(', ')} already ${
    names.length === 1 ? 'has' : 'have'
  } a capability called ${capability.name.trim()} of ${
    names.length === 1 ? 'its' : 'their'
  } own, so it can't apply there. Choose another name, or other roles.`;
};

/** The roles a shared capability of this name could not apply to: their team has one of its own by that name. */
export const clashingRoleIds = (roles: SetupRole[], name: string): Set<string> => {
  const teams = new Set(roles.filter((r) => r.capabilities.some((c) => sameName(c.name, name))).map((r) => r.teamId));
  return new Set(roles.filter((r) => teams.has(r.teamId)).map((r) => r.id));
};

/**
 * Why a role's own capability can't be called this, or null when it can: one
 * of the shared capabilities that applies in this team (to any of its roles)
 * already has the name. Whether or not its set is confirmed yet.
 */
export const sharedNameProblem = (
  roles: SetupRole[],
  sets: CapabilitySet[],
  role: SetupRole,
  name: string
): string | null => {
  if (!name.trim()) return null;
  const team = roles.filter((r) => r.teamId === role.teamId);
  for (const set of sets) {
    const hit = set.capabilities.find((c) => sameName(c.name, name) && team.some((r) => appliesTo(c, r)));
    if (hit) {
      return `${hit.name} is a ${
        SOURCE_LABEL[set.kind]
      } capability your organization sets for roles in this team. Choose another name.`;
    }
  }
  return null;
};

/** What a save of a set sends besides its version: only what changed. `capabilities`, when sent, replaces the list. */
export interface SetUpdate {
  capabilities?: Array<{ id: string; name: string; criteria: LevelCriteria; roleIds: string[]; allRoles: boolean }>;
  capabilitiesConfirmed?: boolean;
  levelsConfirmed?: boolean;
}

const sent = (set: CapabilitySet) =>
  set.capabilities.map((c) => ({
    id: c.id,
    name: c.name,
    criteria: { weak: '', developing: '', capable: '', advanced: '', ...c.criteria },
    roleIds: [...c.roleIds].sort(),
    allRoles: c.allRoles
  }));

/** The fields that differ between the saved set and the set as it should be, or null when nothing does. */
export const setUpdate = (saved: CapabilitySet, next: CapabilitySet): SetUpdate | null => {
  const update: SetUpdate = {};
  const capabilities = sent(next);
  if (JSON.stringify(capabilities) !== JSON.stringify(sent(saved))) update.capabilities = capabilities;
  if (Boolean(next.capabilitiesConfirmed) !== Boolean(saved.capabilitiesConfirmed)) {
    update.capabilitiesConfirmed = Boolean(next.capabilitiesConfirmed);
  }
  if (Boolean(next.levelsConfirmed) !== Boolean(saved.levelsConfirmed)) {
    update.levelsConfirmed = Boolean(next.levelsConfirmed);
  }
  return Object.keys(update).length > 0 ? update : null;
};

/** The sets as the backend answers them, in the page's shape, with an empty one for a kind it has none of. */
export const setsFromApi = (raw: unknown): CapabilitySet[] => {
  const list = Array.isArray(raw) ? raw : [];
  return (['development', 'standards'] as SharedKind[]).map((kind) => {
    const found = list.find((s: any) => s?.kind === kind);
    if (!found) return emptySet(kind);
    return {
      kind,
      capabilities: (Array.isArray(found.capabilities) ? found.capabilities : []).map((c: any) => ({
        id: String(c.id),
        name: String(c.name ?? ''),
        criteria: c.criteria ?? {},
        roleIds: Array.isArray(c.roleIds) ? c.roleIds.map(String) : [],
        allRoles: Boolean(c.allRoles),
        setBy: c.setBy?.id ? { id: String(c.setBy.id), name: String(c.setBy.name ?? '') } : null
      })),
      capabilitiesConfirmed: Boolean(found.capabilitiesConfirmed),
      levelsConfirmed: Boolean(found.levelsConfirmed),
      established: Boolean(found.established),
      version: Number(found.version) || 0
    };
  });
};
